import React from "react";
import { Box, Stack } from "@mui/material";
import { ParlorDetailProps } from "../../../types";
import { ColorSystemProps } from "../../../constants";
export type FAQKey =
  | "binso_count"
  | "binso_charge"
  | "total_cost"
  | "parking"
  | "how_to_visit"
  | "sangjo"
  | "procedure"
  | "hospital_or_independent"
  | "gongsul_or_private";

export function ETypeFAQ({
  defaultParlor,
  mainColor,
  faqKeys = ["binso_count", "binso_charge", "total_cost", "parking"],
}: {
  defaultParlor: ParlorDetailProps;
  mainColor: ColorSystemProps;
  faqKeys?: FAQKey[];
}) {
  const isGongsul = defaultParlor.managed_type === "공설";
  const parkCnt = Number(defaultParlor.amenities?.parkcnt) || 0;
  const hasParkingLot = !!defaultParlor.amenities?.has_parkinglot;
  const binsos = defaultParlor.binsos || [];
  const expected = (defaultParlor as any).expected_charge_sum || {};
  const mubinso = expected["무빈소"] || 0;
  const gajok = expected["가족장"] || 0;
  const ilban = expected["일반장"] || 0;
  const man = (n: number) => Math.round(n / 10000);

  const allFaqItems: Record<FAQKey, { q: string; a: string }> = {
    binso_count: {
      q: `${defaultParlor.companyname} 빈소는 몇 개이고 크기는 어떻게 되나요?`,
      a: binsos.length
        ? `빈소는 평수별로 ${binsos.length}종류가 존재합니다. ${Math.min(...binsos.map(b => b.pyeong || 0))}평부터 ${Math.max(...binsos.map(b => b.pyeong || 0))}평까지 규모가 다양하며, 조문객 수에 맞춰 선택할 수 있습니다.`
        : `빈소 상세 정보가 공개되지 않았습니다. 정확한 개수와 크기는 고이장례연구소 상담으로 확인 가능합니다.`,
    },
    binso_charge: {
      q: `빈소 사용료는 얼마인가요?`,
      a: binsos.length
        ? `1일 기준 ${binsos.map(b => `${b.pyeong}평 ${(b.charge || 0).toLocaleString("ko-KR")}원`).join(" / ")}. 보통 3일장을 치르시므로, 실제 부담 금액은 3배로 보시면 됩니다.`
        : `빈소별 사용료 정보가 공개되지 않았습니다.`,
    },
    total_cost: {
      q: `전체 장례 예상 비용은 얼마나 되나요?`,
      a: mubinso || gajok || ilban
        ? `장례식장에 지불하는 시설·음식·제단 비용은 무빈소 약 ${man(mubinso)}만원, 가족장 약 ${man(gajok)}만원, 일반장 약 ${man(ilban)}만원 수준입니다. 전체 장례 비용은 장례식장 기본 지출과 상조 상품(제단·음식·인력) 여부에 따라 달라집니다.`
        : `예상 비용 데이터가 공개되지 않았습니다.`,
    },
    parking: {
      q: `주차 공간은 충분한가요?`,
      a: hasParkingLot
        ? parkCnt
          ? `전용 주차면 약 ${parkCnt}대가 확보되어 있습니다${parkCnt < 10 ? " (공설 장례식장 특성상 협소할 수 있으니 조문객에게 대중교통·인근 공영주차장을 함께 안내하는 것이 좋습니다)" : ""}.`
          : `주차장 이용이 가능하나 정확한 대수가 공개되지 않았습니다.`
        : `e하늘 장사정보시스템에 주차 정보가 등록되어 있지 않습니다. 정확한 주차 가능 여부는 장례식장에 직접 문의하시기 바랍니다.`,
    },
    how_to_visit: {
      q: `예약 없이 방문해도 되나요?`,
      a: `장례식장은 사전 예약 없이도 방문 가능합니다. 다만 빈소 배정은 임종 후 결정되므로, 미리 비용과 시설을 비교해두고 실제 장례 발생 시 고이장례연구소를 통해 빠르게 진행하는 것을 권장합니다.`,
    },
    sangjo: {
      q: `상조 없이도 장례를 치를 수 있나요?`,
      a: `장례 진행에 필수인 음식·시설 이용료는 장례식장 비용이므로 상조 없이도 장례를 진행하실 수 있습니다. 단 상조를 통해 진행하는 경우 제단·꽃·장례용품(수의·관·유골함 등)을 패키지로 구매할 수 있어 비교해보시는 것도 좋습니다.`,
    },
    procedure: {
      q: `3일장 기준 장례 일정이 어떻게 되나요?`,
      a: `1일차는 임종·안치·수시·입관, 2일차는 빈소 운영·조문 접객, 3일차는 발인·화장(또는 매장)·안장 순으로 진행됩니다. 정확한 일정과 필요 서류는 고이장례연구소 전문가가 처음부터 안내해드립니다.`,
    },
    hospital_or_independent: {
      q: `${defaultParlor.companyname}은 병원 부설 장례식장인가요, 독립 운영 장례식장인가요?`,
      a:
        defaultParlor.companyname.includes("병원") ||
        defaultParlor.companyname.includes("의료원")
          ? `${defaultParlor.companyname}은 병원·의료원 부설 장례식장입니다. 임종 직후 안치까지의 동선이 동일 건물 내에서 처리되어 이송 거리·시간이 줄어드는 구조입니다. 빈소 ${binsos.length || "-"}개 규모와 시설 안내를 함께 확인하시면 됩니다.`
          : `${defaultParlor.companyname}은 독립 운영 장례식장입니다. 임종지가 자택·요양원인 경우 이송 거리를 미리 검토해두시면 좋고, 시설·빈소 ${binsos.length || "-"}개 구성과 조문객 규모에 맞춰 선택하시면 됩니다.`,
    },
    gongsul_or_private: {
      q: `${defaultParlor.companyname}은 공설 장례식장인가요?`,
      a: isGongsul
        ? `${defaultParlor.companyname}은 지자체·공공의료원이 운영하는 공설 장례식장입니다. 빈소 사용료는 공개 단가 기준으로 안내되며, 일부 지자체 보조 항목이 적용되는 경우가 있어 거주지 지자체에 추가 안내 여부를 확인해보시는 것을 권합니다.`
        : `${defaultParlor.companyname}은 사립 장례식장입니다. 빈소 구성·부대 서비스 안내는 e하늘 장사정보시스템에 공개된 정보를 기준으로 안내드리며, 자세한 상조 패키지·의전 인력 구성은 견적 단계에서 확인 가능합니다.`,
    },
  };

  const faqs = [
    ...faqKeys.map(key => allFaqItems[key]),
    ...(isGongsul
      ? [{
          q: `공설 장례식장의 특성은 무엇인가요?`,
          a: `${defaultParlor.companyname}은 공설 장례식장으로, 비용 체계가 공개적이며 지자체 연계로 안정 운영됩니다. 의료원 부설인 경우 임종-안치 동선이 유리합니다. 다만 매점이나 유족 대기실이 별도 지정되지 않은 경우가 있으므로 시설 안내를 미리 확인하세요.`,
        }]
      : []),
  ].map((faq, i) => ({ ...faq, open: i === 0 }));

  return (
    <Stack padding="40px 20px">
      <Box
        component="h2"
        style={{ fontSize: "24px", fontWeight: 700, color: "#252525" }}
      >
        {defaultParlor.companyname} 자주 묻는 질문
      </Box>

      <Box
        style={{
          marginTop: "28px",
          borderTop: "1px solid #dedede",
        }}
      >
        {faqs.map((faq, i) => (
          <details key={i} open={faq.open} style={{ borderBottom: "1px solid #dedede" }}>
            <summary
              style={{
                padding: "22px 0",
                fontSize: "17px",
                fontWeight: 700,
                color: "#252525",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "16px",
                listStyle: "none",
              }}
            >
              <span>{faq.q}</span>
              <span
                style={{
                  fontSize: "22px",
                  color: mainColor.c400,
                  fontWeight: 400,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                +
              </span>
            </summary>
            <Box
              component="div"
              style={{
                paddingBottom: "22px",
                fontSize: "15px",
                lineHeight: 1.8,
                color: "#535353",
              }}
            >
              {faq.a}
            </Box>
          </details>
        ))}
      </Box>
    </Stack>
  );
}
